import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  XMarkIcon,
  UserCircleIcon,
  UserIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/solid";
import toast from "react-hot-toast";
import { auth, rtdb, firebaseConfig } from "../../config/firebase";
import { ref, update, set } from "firebase/database";
import { initializeApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";

const roles = [
  { id: "Admin", label: "Admin", icon: UserCircleIcon },
  { id: "Pengguna", label: "Pengguna", icon: UserIcon },
];

const initialFormState = {
  nama: "",
  email: "",
  password: "",
  role: "Pengguna",
};

export default function ModalAkun({ data, onClose, onSave }) {
  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (data) {
      setForm({
        ...initialFormState,
        ...data,
        role: data.role || "Pengguna",
        password: "",
      });
    } else {
      setForm(initialFormState);
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.nama.trim()) newErrors.nama = "Nama tidak boleh kosong.";
    if (!form.email.trim()) newErrors.email = "Email tidak boleh kosong.";
    if (!data && !form.password) newErrors.password = "Password tidak boleh kosong.";
    if (form.password && form.password.length < 6)
      newErrors.password = "Password minimal 6 karakter.";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;

    const formErrors = validate();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      toast.error("Harap periksa kembali, data belum valid.");
      return;
    }

    setIsSaving(true);
    const toastId = toast.loading("Menyimpan data...");

    try {
      const dataToSave = {
        nama: form.nama.trim(),
        email: form.email.trim(),
        role: form.role,
      };

      const secondaryApp = initializeApp(firebaseConfig, "Secondary");
      const secondaryAuth = getAuth(secondaryApp);

      if (data && data.id) {
        await update(ref(rtdb, `akun/${data.id}`), dataToSave);
        if (form.password) {
          await sendPasswordResetEmail(auth, form.email.trim());
          toast.success("Link reset password dikirim ke email user.", { id: toastId });
        } else {
          toast.success("Akun berhasil diperbarui!", { id: toastId });
        }
      } else {
        const userCredential = await createUserWithEmailAndPassword(
          secondaryAuth,
          form.email.trim(),
          form.password
        );
        const uid = userCredential.user.uid;
        await set(ref(rtdb, `akun/${uid}`), {
          id: uid,
          ...dataToSave,
          createdAt: new Date().toISOString(),
        });
        toast.success("Akun baru berhasil dibuat!", { id: toastId });
      }

      onSave(form);
      setTimeout(onClose, 900);
    } catch (error) {
      console.error("❌ Gagal menyimpan data:", error);
      if (error.code === "auth/email-already-in-use") {
        toast.error("Email sudah terdaftar.", { id: toastId });
      } else {
        toast.error("Gagal menyimpan data. Coba lagi.", { id: toastId });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const inputBase =
    "w-full pl-10 pr-3 py-2 text-sm border rounded-md shadow-sm focus:outline-none focus:ring-2 transition";
  const errorInput = "border-red-500 ring-red-500";
  const normalInput = "border-gray-300 focus:ring-green-500 focus:border-green-500";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col my-auto"
      >
        <div className="flex justify-between items-center p-5 border-b bg-white rounded-t-xl">
          <h2 className="text-lg font-bold text-gray-800">
            {data ? "Edit Akun" : "Tambah Akun Baru"}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="relative">
            <UserIcon
              className={`w-5 h-5 absolute left-3 top-2.5 ${
                errors.nama ? "text-red-400" : "text-gray-400"
              }`}
            />
            <input
              type="text"
              name="nama"
              placeholder="Nama Lengkap"
              value={form.nama}
              onChange={handleChange}
              className={`${inputBase} ${errors.nama ? errorInput : normalInput}`}
            />
          </div>

          <div className="relative">
            <UserCircleIcon
              className={`w-5 h-5 absolute left-3 top-2.5 ${
                errors.email ? "text-red-400" : "text-gray-400"
              }`}
            />
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={form.email}
              onChange={handleChange}
              className={`${inputBase} ${errors.email ? errorInput : normalInput}`}
            />
          </div>

          <div className="relative">
            <LockClosedIcon
              className={`w-5 h-5 absolute left-3 top-2.5 ${
                errors.password ? "text-red-400" : "text-gray-400"
              }`}
            />
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder={data ? "Kosongkan jika tidak diubah" : "Password"}
              value={form.password}
              onChange={handleChange}
              className={`${inputBase} pr-10 ${errors.password ? errorInput : normalInput}`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3"
            >
              {showPassword ? (
                <EyeSlashIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <EyeIcon className="h-5 w-5 text-gray-400" />
              )}
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">Pilih Role</label>
            <div className="grid grid-cols-2 gap-3">
              {roles.map((role) => {
                const isSelected = form.role === role.id;
                return (
                  <button
                    type="button"
                    key={role.id}
                    onClick={() => setForm((prev) => ({ ...prev, role: role.id }))}
                    className={`flex flex-col items-center p-3 border-2 rounded-lg transition ${
                      isSelected
                        ? "text-green-700 border-green-500 bg-green-50"
                        : "text-gray-500 hover:border-gray-400"
                    }`}
                  >
                    <role.icon
                      className={`w-6 h-6 mb-1 ${
                        isSelected ? "text-green-600" : "text-gray-400"
                      }`}
                    />
                    <span className="text-xs font-semibold">{role.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex items-center justify-center min-w-[110px] px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:bg-green-400"
            >
              {isSaving && <ArrowPathIcon className="animate-spin h-5 w-5 mr-2" />}
              {isSaving ? "Menyimpan..." : "Simpan"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
